import apiClient, { TOKEN_KEYS } from './client';
import { getFingerprint, hasCachedFingerprint } from '../utils/fingerprint';
import type { LoginRequest, RegisterRequest, AuthResponse } from '../types';

// Login user
export async function login(data: LoginRequest): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/login', data);

  // Store tokens
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.access_token);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, response.data.refresh_token);

  return response.data;
}

export async function loginWithGoogleOneTap(credential: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/oauth/google/one-tap', { credential });

  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.access_token);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, response.data.refresh_token);

  return response.data;
}

// Register new user
export async function register(data: RegisterRequest): Promise<AuthResponse> {
  // Include fingerprint to link any anonymous data
  let requestData = { ...data };
  if (hasCachedFingerprint()) {
    try {
      requestData.fingerprint = await getFingerprint();
    } catch {
      // Fingerprint is optional, continue without it
    }
  }

  const response = await apiClient.post<AuthResponse>('/auth/register', requestData);

  // Store tokens
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.access_token);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, response.data.refresh_token);

  return response.data;
}

// Logout current session
export async function logout(): Promise<void> {
  try {
    await apiClient.post('/auth/logout');
  } finally {
    // Always clear tokens, even if API call fails
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  }
}

// Logout from all devices
export async function logoutAll(): Promise<void> {
  try {
    await apiClient.post('/auth/logout-all');
  } finally {
    localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  }
}

// Refresh access token
export async function refreshToken(refresh_token: string): Promise<AuthResponse> {
  const response = await apiClient.post<AuthResponse>('/auth/refresh', { refresh_token });

  // Store new tokens
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, response.data.access_token);
  if (response.data.refresh_token) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, response.data.refresh_token);
  }

  return response.data;
}

// Request password reset
export interface ForgotPasswordRequest {
  email: string;
}

export async function forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/forgot-password', data);
  return response.data;
}

// Reset password
export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export async function resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/reset-password', data);
  return response.data;
}

// Verify email
export interface VerifyEmailRequest {
  token: string;
}

export async function verifyEmail(data: VerifyEmailRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/verify-email', data);
  return response.data;
}

// Resend verification email
export interface ResendVerificationRequest {
  email: string;
}

export async function resendVerification(data: ResendVerificationRequest): Promise<{ message: string }> {
  const response = await apiClient.post<{ message: string }>('/auth/resend-verification', data);
  return response.data;
}
